import React, { useState, useEffect } from 'react';
import { createCareer, updateCareer, getCategories } from '../../services/api';
import { useImageUpload } from '../hooks/useImageUpload';
import { textToArray, arrayToText, getFullImageUrl } from '../utils/helpers';
import toast from 'react-hot-toast';

const CareerForm = ({ editingItem, onClose, onRefresh, careerCategories = [] }) => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  
  const [formData, setFormData] = useState({
    title: editingItem?.title || '',
    department: editingItem?.department || (careerCategories[0]?.name || ''),
    location: editingItem?.location || 'Remote',
    type: editingItem?.type || 'Full-time',
    experience: editingItem?.experience || '',
    salary: editingItem?.salary || 'Negotiable',
    description: editingItem?.description || '',
    requirements: arrayToText(editingItem?.requirements || []),
    responsibilities: arrayToText(editingItem?.responsibilities || []),
    benefits: arrayToText(editingItem?.benefits || []),
    imageUrl: editingItem?.imageUrl || '',
    order: editingItem?.order || 0,
    isActive: editingItem?.isActive !== false
  });
  
  const [loading, setLoading] = useState(false);
  
  // Use the image upload hook
  const { 
    imageFile, 
    uploading, 
    preview, 
    handleImageChange, 
    uploadImage,
    setImageFile 
  } = useImageUpload();

  // Employment Type Options
  const employmentTypes = [
    { value: 'Full-time', label: 'Full-time', icon: '💼' },
    { value: 'Part-time', label: 'Part-time', icon: '⏰' },
    { value: 'Contract', label: 'Contract', icon: '📝' },
    { value: 'Internship', label: 'Internship', icon: '🎓' },
    { value: 'Remote', label: 'Remote', icon: '🏠' },
    { value: 'Freelance', label: 'Freelance', icon: '🖥️' }
  ];

  // Location Options
  const locationOptions = [
    { value: 'Remote', label: 'Remote', icon: '🏠' },
    { value: 'Hybrid', label: 'Hybrid', icon: '🔄' },
    { value: 'On-site', label: 'On-site', icon: '🏢' },
    { value: 'Noida', label: 'Noida, India', icon: '🇮🇳' },
    { value: 'Mumbai', label: 'Mumbai, India', icon: '🇮🇳' },
    { value: 'Bangalore', label: 'Bangalore, India', icon: '🇮🇳' },
    { value: 'Delhi', label: 'Delhi, India', icon: '🇮🇳' },
    { value: 'Pune', label: 'Pune, India', icon: '🇮🇳' },
    { value: 'Chennai', label: 'Chennai, India', icon: '🇮🇳' },
    { value: 'Hyderabad', label: 'Hyderabad, India', icon: '🇮🇳' },
    { value: 'Kolkata', label: 'Kolkata, India', icon: '🇮🇳' },
    { value: 'Gurgaon', label: 'Gurgaon, India', icon: '🇮🇳' },
    { value: 'International', label: 'International', icon: '🌍' }
  ];

  // Experience Options
  const experienceOptions = [
    { value: 'Fresher', label: 'Fresher (0-1 years)', icon: '🌱' },
    { value: 'Junior', label: 'Junior (1-3 years)', icon: '📚' },
    { value: 'Mid-Level', label: 'Mid-Level (3-5 years)', icon: '💪' },
    { value: 'Senior', label: 'Senior (5-8 years)', icon: '🏆' },
    { value: 'Lead', label: 'Lead (8-10 years)', icon: '👔' },
    { value: 'Manager', label: 'Manager (10+ years)', icon: '👨‍💼' },
    { value: 'Director', label: 'Director (12+ years)', icon: '👑' }
  ];

  useEffect(() => {
    if (careerCategories.length > 0) {
      setCategories(careerCategories);
      setLoadingCategories(false);
    } else {
      fetchCategories();
    }
  }, [careerCategories]);

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      let categoriesArray = [];
      
      if (response && response.data && Array.isArray(response.data)) {
        categoriesArray = response.data.filter(cat => cat.type === 'career');
      } else if (response && Array.isArray(response)) {
        categoriesArray = response.filter(cat => cat.type === 'career');
      }
      
      setCategories(categoriesArray);
      
      if (!editingItem && categoriesArray.length > 0 && !formData.department) {
        setFormData(prev => ({ ...prev, department: categoriesArray[0].name }));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleAddNewCategory = async () => {
    if (!newCategory.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://inteliheads.in/api'}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          name: newCategory.trim(),
          type: 'career',
          order: categories.length + 1,
          isActive: true
        })
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('Department added successfully');
        setCategories([...categories, { _id: result.data._id, name: newCategory.trim(), type: 'career' }]);
        setFormData({ ...formData, department: newCategory.trim() });
        setNewCategory('');
        setShowNewCategoryInput(false);
        onRefresh();
      } else {
        toast.error(result.message || 'Failed to add department');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Error adding department');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error('Please enter job title');
      return;
    }
    if (!formData.department) {
      toast.error('Please select a department');
      return;
    }
    
    setLoading(true);
    let imageUrl = formData.imageUrl;
    if (imageFile) {
      imageUrl = await uploadImage();
    }
    
    const submitData = {
      title: formData.title,
      department: formData.department,
      location: formData.location,
      type: formData.type,
      experience: formData.experience,
      salary: formData.salary,
      description: formData.description,
      requirements: textToArray(formData.requirements),
      responsibilities: textToArray(formData.responsibilities),
      benefits: textToArray(formData.benefits),
      imageUrl: imageUrl,
      order: parseInt(formData.order) || 0,
      isActive: formData.isActive
    };
    
    try {
      if (editingItem && editingItem._id) {
        await updateCareer(editingItem._id, submitData);
        toast.success('Job posting updated successfully');
      } else {
        await createCareer(submitData);
        toast.success('Job posting created successfully');
      }
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error saving career:', error);
      toast.error(error.message || 'Error saving job posting');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (categoryName) => {
    const category = categories.find(c => c.name === categoryName);
    return category?.icon || '💼';
  };

  if (loadingCategories) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '500px', width: '90%', textAlign: 'center' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#2C7A7B' }}></i>
          <p style={{ marginTop: '1rem' }}>Loading departments...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '800px', width: '90%', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#0A192F', margin: 0 }}>{editingItem ? 'Edit Job Posting' : 'Add New Job Posting'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* Job Title */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Job Title *</label>
            <input 
              type="text" 
              required 
              value={formData.title} 
              onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
              placeholder="e.g., Senior Power Systems Engineer"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Department/Category */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Department *</label>
            <select
              required
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginBottom: '0.5rem' }}
            >
              {categories.length === 0 ? (
                <option value="">No departments available. Please add a department first.</option>
              ) : (
                categories.map((cat) => (
                  <option key={cat._id} value={cat.name}>
                    {cat.icon} {cat.name}
                  </option>
                ))
              )}
            </select>
            
            {!showNewCategoryInput ? (
              <button 
                type="button"
                onClick={() => setShowNewCategoryInput(true)}
                style={{
                  padding: '0.4rem 0.8rem',
                  background: 'transparent',
                  color: '#2C7A7B',
                  border: '1px solid #2C7A7B',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem'
                }}
              >
                <i className="fas fa-plus"></i> Add New Department
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter new department name"
                  style={{ flex: 1, padding: '0.5rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleAddNewCategory}
                  style={{ padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewCategoryInput(false);
                    setNewCategory('');
                  }}
                  style={{ padding: '0.5rem 1rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            )}
            
            {formData.department && categories.length > 0 && (
              <small style={{ color: '#2C7A7B', display: 'block', marginTop: '0.25rem' }}>
                <i className="fas fa-info-circle"></i> Selected: {getCategoryIcon(formData.department)} {formData.department}
              </small>
            )}
          </div>
          
          {/* Location and Employment Type */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Location *</label>
              <select
                required
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
              >
                {locationOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.icon} {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Employment Type *</label>
              <select
                required
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
              >
                {employmentTypes.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.icon} {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Experience and Salary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Experience Required</label>
              <select
                value={formData.experience}
                onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
              >
                <option value="">Select experience level</option>
                {experienceOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.icon} {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Salary Range</label>
              <input 
                type="text" 
                value={formData.salary} 
                onChange={(e) => setFormData({ ...formData, salary: e.target.value })} 
                placeholder="$50,000 - $70,000"
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
            </div>
          </div>
          
          {/* Order and Active Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Display Order</label>
              <input 
                type="number" 
                value={formData.order} 
                onChange={(e) => setFormData({ ...formData, order: e.target.value })} 
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
              <small style={{ color: '#5A6E7F' }}>Lower numbers appear first</small>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={formData.isActive} 
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} 
                />
                <span>Active</span>
              </label>
            </div>
          </div>
          
          {/* Job Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Job Description</label>
            <textarea 
              value={formData.description} 
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
              rows="4" 
              placeholder="Detailed job description..."
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Requirements */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Requirements (one per line)</label>
            <textarea 
              value={formData.requirements} 
              onChange={(e) => setFormData({ ...formData, requirements: e.target.value })} 
              rows="4" 
              placeholder="Bachelor's degree in Engineering&#10;3+ years of experience&#10;Knowledge of power systems"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Responsibilities */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Responsibilities (one per line)</label>
            <textarea 
              value={formData.responsibilities} 
              onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })} 
              rows="4" 
              placeholder="Design and develop solutions&#10;Lead technical discussions&#10;Mentor junior developers"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Benefits */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Benefits (one per line)</label>
            <textarea 
              value={formData.benefits} 
              onChange={(e) => setFormData({ ...formData, benefits: e.target.value })} 
              rows="3" 
              placeholder="Health insurance&#10;Remote work options&#10;Professional development"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Job Image Upload */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Job Image (Optional)</label>
            
            {(preview || formData.imageUrl) && (
              <div style={{ marginBottom: '0.5rem' }}>
                <img 
                  src={preview || getFullImageUrl(formData.imageUrl)} 
                  alt="Job Preview" 
                  style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #E2E8F0' }} 
                />
              </div>
            )}
            
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{
                display: 'inline-block',
                padding: '0.4rem 0.8rem',
                background: '#2C7A7B',
                color: 'white',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}>
                <i className="fas fa-upload"></i> Choose Image
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={(e) => handleImageChange(e.target.files[0])} 
                  style={{ display: 'none' }} 
                />
              </label>
              <small style={{ color: '#5A6E7F', marginLeft: '0.5rem' }}>Max 5MB, JPG, PNG, GIF, WEBP</small>
            </div>
            
            <input 
              type="text" 
              value={formData.imageUrl} 
              onChange={(e) => {
                setFormData({ ...formData, imageUrl: e.target.value });
                // Don't set imageFile here, just update the URL
              }} 
              placeholder="Or enter image URL directly" 
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
            <small style={{ color: '#5A6E7F', display: 'block', marginTop: '0.25rem' }}>
              Upload a company logo or job-related image
            </small>
          </div>
          
          {/* Submit Buttons */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button 
              type="submit" 
              disabled={loading || uploading} 
              style={{ 
                flex: 1, 
                padding: '0.75rem', 
                background: '#2C7A7B', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontWeight: 600,
                opacity: (loading || uploading) ? 0.7 : 1
              }}
            >
              {loading || uploading ? (
                <><i className="fas fa-spinner fa-spin"></i> Saving...</>
              ) : (
                <><i className="fas fa-save"></i> Save Job Posting</>
              )}
            </button>
            <button 
              type="button" 
              onClick={onClose} 
              style={{ 
                flex: 1, 
                padding: '0.75rem', 
                background: '#5A6E7F', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontWeight: 600 
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CareerForm;